import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { humanSize } from '@/lib/format';

// Reusable resume download card — shared between Home and About page.
// Renders only when an active resume has been uploaded via /admin. Returns
// null otherwise so we never surface a broken 404 legacy hotlink.
const ResumeCard = ({ className = '' }) => {
    const [resume, setResume] = useState(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let alive = true;
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/resume`;
        fetch(url)
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
                if (!alive) return;
                setResume(data);
                setLoaded(true);
            })
            .catch(() => {
                if (!alive) return;
                setLoaded(true);
            });
        return () => {
            alive = false;
        };
    }, []);

    // Hide until fetch settles to avoid layout jump / stale 404 URL flash
    if (!loaded) return null;
    if (!resume || !resume.url) return null;

    const href = `${process.env.REACT_APP_BACKEND_URL}${resume.url}`;
    const filename = resume.filename || 'CV.pdf';
    const meta = `PDF · ${humanSize(resume.size)} · ${filename}`;

    return (
        <a
            href={href}
            target="_blank"
            rel="noreferrer"
            download
            data-testid="resume-card"
            data-cursor="hover"
            data-active-resume="true"
            className={`group relative flex items-center justify-between gap-6 rounded-[22px] border border-[var(--accent)] bg-[var(--accent)] text-[var(--accent-ink)] p-6 md:p-10 overflow-hidden hover:opacity-95 transition-opacity ${className}`}
        >
            <div>
                <div className="font-mono-brand text-[10.5px] uppercase tracking-[0.24em] opacity-70">
                    Resume
                </div>
                <div className="mt-3 font-display leading-[0.95] text-4xl md:text-6xl">
                    Download{' '}
                    <span className="font-display-italic">the CV</span>
                    <span className="opacity-70">.</span>
                </div>
                <div className="mt-3 font-sans-brand text-[13px] opacity-75">
                    {meta}
                </div>
            </div>
            <span className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-full bg-[var(--accent-ink)] text-[var(--accent)] shrink-0 group-hover:scale-105 group-hover:-rotate-6 transition-transform duration-500">
                <Download size={22} />
            </span>
        </a>
    );
};

export default ResumeCard;
